<script setup lang="ts">
import * as Sentry from '@sentry/vue';

import { useUserStore } from './store/user';
import { IMarket } from './types/region';
import { getTimezoneString } from './utils/helper/auth';

import { useAppStore } from '@/store/app';
import { REGIONS, LANGUAGES, DEFAULT_LANGUAGE, DEFAULT_MARKET, APP_NAME, APP_DESCRIPTION } from '@/constants';
import { getIpLocation } from '@/utils/api/app';
import { getAccountLink } from '@/utils/api/auth';
import { gtmInitLanguage } from '@/utils/helper/gtm';

const router = useRouter();
const appStore = useAppStore();
const config = useRuntimeConfig();
const userStore = useUserStore();
const { setLocale } = useI18n();

useHead({
	title: APP_NAME,
	meta: [
		// Primary Meta Tags
		{ name: 'title', content: APP_NAME },
		{ name: 'description', content: APP_DESCRIPTION },
		{
			name: 'keywords',
			content:
				'VNGGames Card, Online gaming card, Gaming prepaid card, VNG prepaid card, Game currency card, VNGGames top-up card, VNGGames gift card, VNGGames voucher, Virtual game card, Gaming payment card, VNGGames card codes, VNGGames card balance, VNGGames card discounts, VNGGames card promotions, Gaming rewards card, VNGGames card redemption, VNGGames membership card, Gaming gift voucher, VNGGames card reseller, VNGGames card retailers',
		},
		{
			name: 'googlebot',
			content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
		},
		{
			name: 'generator',
			content: 'Nuxt 3',
		},
		{
			name: 'copyright',
			content: 'VNG Singapore',
		},
		{
			name: 'author',
			content: 'VNG Singapore',
		},
		{
			name: 'mobile-web-app-capable',
			content: 'yes',
		},
		{
			name: 'apple-mobile-web-app-capable',
			content: 'yes',
		},
		{
			name: 'apple-mobile-web-app-status-bar-style',
			content: 'black',
		},
		{
			name: 'apple-mobile-web-app-title',
			content: APP_NAME,
		},

		// Open Graph / Facebook
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: 'https://shop.vnggames.com/card/' },
		{ property: 'og:title', content: APP_NAME },
		{ property: 'og:site_name', content: APP_NAME },
		{ property: 'og:description', content: APP_DESCRIPTION },
		{ property: 'og:image', content: 'https://shop.vnggames.com/card/images/vnggames-card-thumbnail.png' },
		{ property: 'og:image:height', content: '630' },
		{ property: 'og:image:width', content: '1200' },

		// Twitter
		{ property: 'twitter:card', content: 'summary_large_image' },
		{ property: 'twitter:url', content: 'https://shop.vnggames.com/card/' },
		{ property: 'twitter:title', content: APP_NAME },
		{ property: 'twitter:description', content: APP_DESCRIPTION },
		{ property: 'twitter:image', content: 'https://shop.vnggames.com/card/images/vnggames-card-thumbnail.png' },
		{ property: 'twitter:height', content: '630' },
		{ property: 'twitter:width', content: '1200' },
	],
});

let isLoading = ref<boolean>(false);

const HIDE_FOOTER_ROUTES = ['/'];

const userCountry = computed(() => userStore.getUserInfo?.country);
const isLoggedIn = computed(() => userStore.getLoggedIn);

const userMarket = computed(() => appStore.getMarket);
const isShowUserMenu = computed(() => appStore.getUserMenuState);
const isShowHeaderMenu = computed(() => appStore.getHeaderMenuState);
const userLanguage = computed(() => appStore.getLanguage);
const detectedLanguage = computed(() => appStore.getDetectedLanguage);
const timezoneString = computed(() => appStore.getTimezoneString);

const currentRoute = computed(() => router.currentRoute.value.path || '');
const isHideFooter = computed(() => HIDE_FOOTER_ROUTES.includes(currentRoute.value));
const accountUrl = computed(() => appStore.getMyAccountUrl);

const findMarket = (value?: string) => {
	const _country = REGIONS.find((item: IMarket) => item.value === value);
	if (_country) return _country;
	return {
		value: '',
		label: '',
		flag: '',
	};
};

const setMarket = () => {
	if (!userMarket.value.value) {
		const market = findMarket(userCountry.value);
		appStore.updateMarket(market);
	}
};

const detectCountryFromIP = async () => {
	if (detectedLanguage.value) return;

	const _detectedLanguage = await getIpLocation();

	appStore.setDetectedLanguage(_detectedLanguage);

	const detectedMarket = REGIONS.find((item: IMarket) => item.value === _detectedLanguage);

	if (detectedMarket) {
		const market = findMarket(detectedMarket.value);
		appStore.updateMarket(market);
	} else {
		appStore.updateMarket(DEFAULT_MARKET);
	}
};

const getCenterLink = async () => {
	if (accountUrl.value) {
		return;
	}
	try {
		const res = await getAccountLink(userLanguage.value.value);
		if (res?.centerLink) {
			appStore.updateMyAccountUrl(res.centerLink);
		}
	} catch (error) {
		console.log({ error });
		Sentry.captureException(error);
	}
};

watch(
	isLoggedIn,
	(val) => {
		if (val) {
			getCenterLink();
		}
		setMarket();
	},
	{ immediate: true },
);

watch(
	timezoneString,
	() => {
		const timezone = getTimezoneString();
		appStore.updateTimezoneString(timezone);
	},
	{ immediate: true },
);

onBeforeMount(async () => {
	await detectCountryFromIP();
});

onMounted(async () => {
	try {
		window.dataLayer.push({
			gaMeasurementId: config.GA_MEASUREMENT_ID,
		});
	} catch (error) {
		console.log({ error });
		Sentry.captureException(error);
	}

	try {
		await detectCountryFromIP();
	} catch (error) {
		console.log({ error });

		if (config.SENTRY_ENVIRONMENT !== 'localhost') {
			Sentry.captureException(error);
		}
	}

	if (userLanguage.value.value === DEFAULT_LANGUAGE.value) {
		const language = LANGUAGES.find((lang) => lang.value === detectedLanguage.value);

		if (language) {
			appStore.updateLanguage(language);
			setLocale(language.value);
		} else {
			setLocale(DEFAULT_LANGUAGE.value);
		}
	}
	gtmInitLanguage(userLanguage.value.value);
});
</script>

<template>
	<main v-loading.fullscreen.lock="isLoading" class="common-layout">
		<el-container class="min-height-full">
			<el-header height="70">
				<Header />
				<change-domain-toast />
			</el-header>
			<el-main>
				<mobile-header-menu :class="{ 'is-opened': isShowHeaderMenu }"></mobile-header-menu>
				<mobile-user-menu :class="{ 'is-opened': isShowUserMenu }"></mobile-user-menu>
				<NuxtLayout>
					<Transition name="page" mode="out-in">
						<div :key="$route.fullPath">
							<NuxtPage />
						</div>
					</Transition>
				</NuxtLayout>
			</el-main>
			<el-footer v-if="!isHideFooter">
				<Footer />
			</el-footer>
		</el-container>
	</main>
</template>

<style lang="scss">
@import '@/assets/scss/app.scss';
</style>
